import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

/**
 * TOPUP REQUESTS — the staff side, one screen for both jobs.
 *
 * Accounts' job (staff holding canApproveTopups, at /agent/topup) is to say
 * "the money arrived"; an admin's job (at /admin/topup) is to approve the BDT
 * into the member's Shopping Balance (1:1, no points). Same screen, two
 * scopes, and the server enforces both:
 *
 *   Accounts  sees pending requests; verifies or rejects them.
 *   Admin     sees only what Accounts has verified; approves or rejects it.
 *             A pending request never appears here — not in the list, the
 *             tabs or the counts.
 *
 * Built for somebody sitting on it all day. The queue is tabs with live counts
 * rather than a dropdown, a waiting request can be verified straight from its
 * row without opening anything, and the list refreshes itself so an agent is
 * never working from a stale screen. Every decision goes through one confirm
 * box that repeats what is about to happen, because these move real money.
 *
 * The detail panel deliberately shows where the request came from — IP, device
 * and the position worked out from it — because that is what a disputed cash
 * payment is argued over.
 */

const STATUS_META = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Waiting for Accounts' },
  verified: { bg: '#dbeafe', color: '#1d4ed8', label: 'Verified by Accounts — waiting for admin' },
  approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  cancelled: { bg: '#f3f4f6', color: '#374151', label: 'Cancelled' },
  undone: { bg: '#fde68a', color: '#78350f', label: 'Undone — moved back to Available Balance' },
};

const METHODS = [
  { value: '', label: 'Any method' },
  { value: 'cash', label: 'Cash' },
  { value: 'mobile_banking', label: 'Mobile banking' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
];

const METHOD_ICON = {
  cash: 'fa-money',
  mobile_banking: 'fa-mobile',
  bank_transfer: 'fa-university',
  other: 'fa-ellipsis-h',
};

/** One click fills the note, because these four are almost every rejection. */
const REJECT_REASONS = [
  'No payment received',
  'Wrong transaction id',
  'Amount does not match',
  'Duplicate request',
];

const VALID_STATUSES = ['', 'pending', 'verified', 'approved', 'rejected', 'cancelled', 'undone'];
/** What the admin view can show: only what Accounts has verified, and after. */
const ADMIN_STATUSES = ['', 'verified', 'approved', 'rejected', 'undone'];

const fmt = (n) => Number(n || 0).toLocaleString();

/** "12 min ago" reads faster than a timestamp when you are working a queue. */
const timeAgo = (value) => {
  const then = new Date(value).valueOf();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString();
};

export default function TopupManageScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const { pathname } = useLocation();
  // Which side of the workflow this page is. /admin/topup is the approval
  // queue (admins only); /agent/topup — or anybody who is not an admin — is
  // the Accounts queue. `isAdmin` below means "this page is the admin view".
  const scope = userInfo?.isAdmin && pathname.startsWith('/admin/') ? 'admin' : 'accounts';
  const isAdmin = scope === 'admin';
  /** Set when the server says this person is not part of Accounts. */
  const [denied, setDenied] = useState('');
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }),
    [userInfo]
  );

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pages: 1, count: 0, totals: null });
  /** Counts across everything, so the tab numbers do not move when you filter. */
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [loadedAt, setLoadedAt] = useState(null);
  const [live, setLive] = useState(true);
  /** TEMPORARY: Developers → Topup undo. The server says whether it is on. */
  const [undoEnabled, setUndoEnabled] = useState(false);

  // A link can land here already filtered — the agent dashboard's cards are
  // two, and a bookmarked filter is another. An agent with no link starts on
  // the only thing they can act on.
  const [urlQuery] = useSearchParams();
  const initial = useMemo(() => {
    const asked = urlQuery.get('status') || '';
    // An admin never sees pending or cancelled requests — they are still with
    // Accounts (or never left it) — so a link asking for one lands on the queue.
    const allowed = isAdmin ? ADMIN_STATUSES : VALID_STATUSES;
    const status = allowed.includes(asked) ? asked : '';
    // Everyone lands on their own queue: Accounts on what they must check, an
    // admin on what Accounts has already verified and is waiting on them.
    return {
      status: status || (isAdmin ? 'verified' : 'pending'),
      method: '',
      username: '',
      q: '',
      from: '',
      to: '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);
  const [filters, setFilters] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [moreFilters, setMoreFilters] = useState(false);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  /** { kind: 'verify' | 'approve' | 'reject' | 'bulk', row } — the confirm box. */
  const [confirm, setConfirm] = useState(null);
  /** Ids ticked for one bulk approval. Admin only, verified requests only. */
  const [picked, setPicked] = useState([]);
  const [bulkAt, setBulkAt] = useState(null);
  /** 'requests' or the supervisor transfer ledger, which is admin-only. */
  const [view, setView] = useState('requests');
  const [shares, setShares] = useState({ rows: [], count: 0 });
  const [shareQuery, setShareQuery] = useState('');

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), pageSize: '20', scope });
        for (const [key, value] of Object.entries(applied)) if (value) params.set(key, value);
        const { data } = await Axios.get(api + `/api/topup/admin/list?${params}`, auth);
        setRows(data.rows || []);
        setUndoEnabled(Boolean(data.undoEnabled));
        setMeta({ page: data.page, pages: data.pages, count: data.count, totals: data.totals });
        setLoadedAt(new Date());
        setDenied('');
      } catch (error) {
        if (error?.response?.status === 403) setDenied(getError(error));
        else if (!quiet) toast.error(getError(error));
      } finally {
        setLoading(false);
      }
    },
    [applied, page, auth, scope]
  );

  // Unfiltered counts for the tabs and the cards along the top. The queue
  // endpoint hands these back whatever the filter is, which is the point.
  const loadTotals = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + `/api/topup/queue?pageSize=1&scope=${scope}`, auth);
      setTotals(data.totals || null);
    } catch (error) {
      /* the headline numbers are a convenience, not the screen */
    }
  }, [auth, scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  // Requests arrive while you are looking at the screen. Refresh quietly, and
  // never while a confirm box is open — the numbers under it must not move.
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => {
      if (confirm || busy) return;
      load({ quiet: true });
      loadTotals();
    }, 30000);
    return () => clearInterval(timer);
  }, [live, confirm, busy, load, loadTotals]);

  const loadShares = useCallback(async () => {
    try {
      const params = new URLSearchParams({ pageSize: '30' });
      if (shareQuery.trim()) params.set('q', shareQuery.trim());
      const { data } = await Axios.get(api + `/api/topup/admin/shares?${params}`, auth);
      setShares({ rows: data.rows || [], count: data.count || 0 });
    } catch (error) {
      toast.error(getError(error));
    }
  }, [auth, shareQuery]);

  useEffect(() => {
    if (view === 'transfers') loadShares();
  }, [view, loadShares]);

  /**
   * TEMPORARY: take an approved topup back — the same Tk leaves the member's
   * Shopping Balance and goes into their Available Balance. Nothing else moves.
   */
  const undoTopup = async (row) => {
    if (
      !window.confirm(
        `Undo this topup?\n\nTk ${fmt(row.amountBdt)} will be taken out of @${row.username}'s Shopping Balance ` +
          `and the same Tk ${fmt(row.amountBdt)} added to their Available Balance.`
      )
    )
      return;
    try {
      setBusy(true);
      const { data } = await Axios.post(api + `/api/topup/${row._id}/undo`, {}, auth);
      toast.success(data.message);
      load({ quiet: true });
      loadTotals();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy(false);
    }
  };

  /** Grant or take away the supervisor flag without leaving the request. */
  const toggleSupervisor = async () => {
    if (!member || !request) return;
    const turningOn = !member.isSuperVisor;
    if (
      !window.confirm(
        turningOn
          ? `Let ${request.username} pass shopping balance down their own downline?`
          : `Stop ${request.username} from passing shopping balance to their downline?`
      )
    )
      return;
    try {
      setBusy(true);
      const { data } = await Axios.post(
        api + '/api/topup/admin/supervisor',
        { userId: request.userId, isSuperVisor: turningOn },
        auth
      );
      toast.success(data.message);
      const { data: fresh } = await Axios.get(api + `/api/topup/detail/${request._id}?scope=${scope}`, auth);
      setDetail(fresh);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (row) => {
    setSelected(row._id);
    setDetail(null);
    setNote('');
    try {
      const { data } = await Axios.get(api + `/api/topup/detail/${row._id}?scope=${scope}`, auth);
      setDetail(data);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const act = async (kind, id) => {
    if (!id) return;
    const body = kind === 'reject' ? { reason: note } : { note };
    try {
      setBusy(true);
      const { data } = await Axios.post(api + `/api/topup/${id}/${kind}`, body, auth);
      toast.success(data.message);
      setConfirm(null);
      setNote('');
      await Promise.all([load({ quiet: true }), loadTotals()]);
      if (selected === id) {
        const { data: fresh } = await Axios.get(api + `/api/topup/detail/${id}?scope=${scope}`, auth);
        setDetail(fresh);
      }
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Approve everything ticked, one request at a time.
   *
   * There is no bulk endpoint and there should not be — each approval is its
   * own guarded credit, and the server's double-credit claim only works per
   * request. So this is a loop that keeps going when one fails, and says at the
   * end exactly how many went through.
   */
  const approveMany = async () => {
    const targets = rows.filter((r) => picked.includes(r._id));
    if (!targets.length) return;
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (let i = 0; i < targets.length; i += 1) {
      setBulkAt({ done: i, total: targets.length });
      try {
        await Axios.post(api + `/api/topup/${targets[i]._id}/approve`, { note }, auth);
        ok += 1;
      } catch (error) {
        failed.push(`@${targets[i].username}: ${getError(error)}`);
      }
    }
    setBulkAt(null);
    setBusy(false);
    setConfirm(null);
    setPicked([]);
    setNote('');
    if (ok) toast.success(`${ok} topup(s) approved.`);
    failed.forEach((line) => toast.error(line));
    await Promise.all([load({ quiet: true }), loadTotals()]);
  };

  /** Only a verified request can be swept up in a bulk approval. */
  const bulkable = rows.filter((r) => r.status === 'verified');
  const pickedRows = rows.filter((r) => picked.includes(r._id));
  const pickedAmount = pickedRows.reduce((sum, r) => sum + Number(r.amountBdt || 0), 0);
  const togglePick = (id) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );

  /** Ask before anything moves. `row` may come from the list or the panel. */
  const ask = (kind, row) => {
    if (kind === 'reject' && !note.trim()) {
      toast.error('Type a reason first — the member is told this.');
      if (row?._id && selected !== row._id) openDetail(row);
      return;
    }
    setConfirm({ kind, row });
  };

  const applyFilters = () => {
    setPage(1);
    setPicked([]);
    setApplied(filters);
  };

  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });
  const setNow = (key) => (value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setApplied(next);
    setPage(1);
    setPicked([]);
  };
  const setNowFromEvent = (key) => (e) => setNow(key)(e.target.value);

  const copy = (value, what) => {
    if (!value) return;
    navigator.clipboard?.writeText(String(value)).then(
      () => toast.success(`${what} copied.`),
      () => toast.error('Could not copy.')
    );
  };

  const request = detail?.request;
  const member = detail?.member;
  const byStatus = totals?.byStatus || {};

  const TABS = isAdmin
    ? [
        {
          value: 'verified',
          label: 'Waiting for approval',
          icon: 'fa-hourglass-half',
          count: byStatus.verified?.count || 0,
        },
        { value: 'approved', label: 'Approved', icon: 'fa-check', count: byStatus.approved?.count || 0 },
        { value: 'rejected', label: 'Rejected', icon: 'fa-times', count: byStatus.rejected?.count || 0 },
        ...(byStatus.undone?.count
          ? [{ value: 'undone', label: 'Undone', icon: 'fa-undo', count: byStatus.undone.count }]
          : []),
        { value: '', label: 'All history', icon: 'fa-list', count: totals?.total || 0 },
      ]
    : [
        { value: 'pending', label: 'To verify', icon: 'fa-clock-o', count: byStatus.pending?.count || 0 },
        {
          value: 'verified',
          label: 'Verified, with admin',
          icon: 'fa-hourglass-half',
          count: byStatus.verified?.count || 0,
        },
        { value: 'approved', label: 'Approved', icon: 'fa-check', count: byStatus.approved?.count || 0 },
        { value: 'rejected', label: 'Rejected', icon: 'fa-times', count: byStatus.rejected?.count || 0 },
        { value: 'cancelled', label: 'Cancelled', icon: 'fa-ban', count: byStatus.cancelled?.count || 0 },
      ];

  const filtersOn =
    Boolean(applied.q || applied.username || applied.method || applied.from || applied.to);

  return (
    <div className="tm-page">
      <Helmet><title>{isAdmin ? 'Topup Approvals' : 'Topup Verification'}</title></Helmet>
      <style>{CSS}</style>

      {/* ----------------------------------------------------------- header */}
      <div className="tm-head">
        <div>
          <h3>{isAdmin ? 'Topups verified by Accounts — waiting for approval' : 'Topup requests — Accounts'}</h3>
          <div className="tm-muted">
            {isAdmin
              ? 'Only requests Accounts has verified reach you. Approve the BDT into the Shopping Balance, or reject it.'
              : 'Confirm that the money actually arrived, or reject the request. An admin then approves the BDT.'}
          </div>
        </div>
        <div className="tm-head-tools">
          <button
            className={live ? 'tm-btn tm-btn-light tm-live-on' : 'tm-btn tm-btn-light'}
            onClick={() => setLive((v) => !v)}
            title="Refresh the list every 30 seconds"
          >
            <span className="tm-dot" /> {live ? 'Live' : 'Paused'}
          </button>
          <button
            className="tm-btn tm-btn-light"
            onClick={() => {
              load();
              loadTotals();
            }}
          >
            <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
          </button>
          {isAdmin ? (
            <Link className="tm-btn tm-btn-light" to="/admin/topup-settings">
              <i className="fa fa-cog" aria-hidden="true"></i> Settings
            </Link>
          ) : null}
        </div>
      </div>

      {denied ? (
        <div className="tm-warn">
          <i className="fa fa-lock" aria-hidden="true"></i> {denied}
          {userInfo?.isAdmin ? (
            <>
              {' '}Admins approve from <Link to="/admin/topup">the approval queue</Link>.
            </>
          ) : null}
        </div>
      ) : null}

      {/* ------------------------------------------------------ headline cards */}
      <div className="tm-stats">
        {!isAdmin ? (
          <button
            className={applied.status === 'pending' ? 'tm-stat tm-stat-on' : 'tm-stat'}
            onClick={() => setNow('status')('pending')}
          >
            <span className="tm-stat-label">Waiting for Accounts</span>
            <b className="tm-stat-amber">{fmt(byStatus.pending?.count)}</b>
            <small>BDT {fmt(byStatus.pending?.amount)} to check</small>
          </button>
        ) : null}
        <button
          className={applied.status === 'verified' ? 'tm-stat tm-stat-on' : 'tm-stat'}
          onClick={() => setNow('status')('verified')}
        >
          <span className="tm-stat-label">Verified by Accounts — waiting for admin</span>
          <b className="tm-stat-blue">{fmt(byStatus.verified?.count)}</b>
          <small>BDT {fmt(byStatus.verified?.amount)} confirmed</small>
        </button>
        <button
          className={applied.status === 'approved' ? 'tm-stat tm-stat-on' : 'tm-stat'}
          onClick={() => setNow('status')('approved')}
        >
          <span className="tm-stat-label">Approved so far</span>
          <b className="tm-stat-green">BDT {fmt(totals?.approvedAmount)}</b>
          <small>{fmt(byStatus.approved?.count)} topup(s) added to Shopping Balances</small>
        </button>
        <div className="tm-stat tm-stat-plain">
          <span className="tm-stat-label">Refreshed</span>
          <b>{loadedAt ? loadedAt.toLocaleTimeString() : '—'}</b>
          <small>{live ? 'Updating every 30s' : 'Auto refresh paused'}</small>
        </div>
      </div>

      {/* An admin also owns the supervisor ledger — who passed balance to whom.
          It is the same question asked from the other end, so it lives here
          rather than on a screen of its own. */}
      {isAdmin ? (
        <div className="tm-views">
          <button
            className={view === 'requests' ? 'tm-view tm-view-on' : 'tm-view'}
            onClick={() => setView('requests')}
          >
            <i className="fa fa-inbox" aria-hidden="true"></i> Requests
          </button>
          <button
            className={view === 'transfers' ? 'tm-view tm-view-on' : 'tm-view'}
            onClick={() => setView('transfers')}
          >
            <i className="fa fa-share-alt" aria-hidden="true"></i> Supervisor transfers
          </button>
        </div>
      ) : null}

      {view === 'transfers' ? (
        <div className="tm-list">
          <div className="tm-filters">
            <input
              className="tm-input tm-grow"
              placeholder="Search a username or a note"
              value={shareQuery}
              onChange={(e) => setShareQuery(e.target.value)}
              onKeyDown={(e) => (e.key === 'Enter' ? loadShares() : null)}
            />
            <button className="tm-btn tm-btn-primary" onClick={loadShares}>Search</button>
          </div>
          {shares.rows.length === 0 ? (
            <div className="tm-empty">
              <div className="tm-empty-icon"><i className="fa fa-share-alt" aria-hidden="true"></i></div>
              <b>No transfers yet.</b>
              <div className="tm-muted">Supervisors passing balance down their tree appear here.</div>
            </div>
          ) : (
            shares.rows.map((row) => (
              <article key={row._id} className="tm-item">
                <div className="tm-item-icon tm-icon-verified">
                  <i className="fa fa-arrow-right" aria-hidden="true"></i>
                </div>
                <div className="tm-item-main">
                  <div className="tm-item-top">
                    <b className="tm-item-amount">BDT {fmt(row.points)}</b>
                  </div>
                  <div className="tm-item-who">
                    @{row.fromUsername} <span className="tm-muted">&rarr;</span> @{row.toUsername}
                  </div>
                  <div className="tm-item-meta">
                    <span>{timeAgo(row.createdAt)}</span>
                    {row.note ? <span>· {row.note}</span> : null}
                  </div>
                </div>
              </article>
            ))
          )}
          <div className="tm-pager tm-muted">{fmt(shares.count)} transfer(s)</div>
        </div>
      ) : (
      <>
      {/* ------------------------------------------------------------- tabs */}
      <div className="tm-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.value || 'all'}
            className={applied.status === tab.value ? 'tm-tab tm-tab-on' : 'tm-tab'}
            onClick={() => setNow('status')(tab.value)}
          >
            <i className={`fa ${tab.icon}`} aria-hidden="true"></i>
            {tab.label}
            <span className="tm-tab-count">{fmt(tab.count)}</span>
          </button>
        ))}
        <button
          className={moreFilters ? 'tm-tab tm-tab-more tm-tab-on' : 'tm-tab tm-tab-more'}
          onClick={() => setMoreFilters((v) => !v)}
        >
          <i className="fa fa-filter" aria-hidden="true"></i>
          Filters
          {filtersOn ? <span className="tm-tab-count tm-tab-count-live">on</span> : null}
        </button>
      </div>

      {/* --------------------------------------------------------- filters */}
      {moreFilters ? (
        <div className="tm-filters">
          <input
            className="tm-input tm-grow"
            placeholder="Search name, phone, transaction id, note"
            value={filters.q}
            onChange={set('q')}
            onKeyDown={(e) => (e.key === 'Enter' ? applyFilters() : null)}
          />
          <input
            className="tm-input"
            placeholder="Username"
            value={filters.username}
            onChange={set('username')}
            onKeyDown={(e) => (e.key === 'Enter' ? applyFilters() : null)}
          />
          <select className="tm-input" value={filters.method} onChange={setNowFromEvent('method')}>
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <input type="date" className="tm-input" value={filters.from} onChange={setNowFromEvent('from')} />
          <input type="date" className="tm-input" value={filters.to} onChange={setNowFromEvent('to')} />
          <button className="tm-btn tm-btn-primary" onClick={applyFilters}>Search</button>
          {filtersOn ? (
            <button
              className="tm-btn tm-btn-light"
              onClick={() => {
                const cleared = { ...filters, q: '', username: '', method: '', from: '', to: '' };
                setFilters(cleared);
                setApplied(cleared);
                setPage(1);
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {/* One sweep through everything an agent already checked. Admin only —
          an agent has nothing to bulk-do, since verifying is per payment. */}
      {isAdmin && bulkable.length ? (
        <div className={picked.length ? 'tm-bulk tm-bulk-on' : 'tm-bulk'}>
          <label className="tm-check">
            <input
              type="checkbox"
              checked={picked.length > 0 && picked.length === bulkable.length}
              onChange={(e) => setPicked(e.target.checked ? bulkable.map((r) => r._id) : [])}
            />
            Select all {bulkable.length} verified on this page
          </label>
          {picked.length ? (
            <>
              <span className="tm-muted">
                {picked.length} picked · BDT {fmt(pickedAmount)}
              </span>
              <button className="tm-btn tm-btn-light tm-btn-sm" onClick={() => setPicked([])}>
                Clear
              </button>
              <button
                className="tm-btn tm-btn-green tm-btn-sm"
                disabled={busy}
                onClick={() => setConfirm({ kind: 'bulk' })}
              >
                <i className="fa fa-check-circle" aria-hidden="true"></i> Approve {picked.length}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      <div className={selected ? 'tm-split tm-split-open' : 'tm-split'}>
        {/* ------------------------------------------------------- the queue */}
        <div className="tm-list">
          {loading ? (
            <div className="tm-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="tm-empty">
              <div className="tm-empty-icon"><i className="fa fa-check-circle" aria-hidden="true"></i></div>
              <b>Nothing here.</b>
              <div className="tm-muted">
                {applied.status === 'pending'
                  ? 'Every payment has been checked. Well done.'
                  : isAdmin && applied.status === 'verified'
                  ? 'Nothing verified by Accounts is waiting for you.'
                  : 'No request matches this view.'}
              </div>
            </div>
          ) : (
            rows.map((row) => {
              const style = STATUS_META[row.status] || STATUS_META.pending;
              const on = selected === row._id;
              return (
                <article
                  key={row._id}
                  className={on ? 'tm-item tm-item-on' : 'tm-item'}
                  onClick={() => openDetail(row)}
                >
                  {isAdmin && row.status === 'verified' ? (
                    <input
                      type="checkbox"
                      className="tm-item-check"
                      checked={picked.includes(row._id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => togglePick(row._id)}
                      title="Pick for a bulk approval"
                    />
                  ) : null}
                  <div className={`tm-item-icon tm-icon-${row.status}`}>
                    <i className={`fa ${METHOD_ICON[row.method] || 'fa-credit-card'}`} aria-hidden="true"></i>
                  </div>

                  <div className="tm-item-main">
                    <div className="tm-item-top">
                      <b className="tm-item-amount">BDT {fmt(row.amountBdt)}</b>
                      <span className="tm-chip" style={{ background: style.bg, color: style.color }}>
                        {style.label}
                      </span>
                    </div>
                    <div className="tm-item-who">
                      {row.name} <span className="tm-muted">@{row.username}</span>
                    </div>
                    <div className="tm-item-meta">
                      <span className="tm-cap">{row.method.replace('_', ' ')}</span>
                      {row.methodDetails?.transactionId ? (
                        <span title="Transaction id">· {row.methodDetails.transactionId}</span>
                      ) : null}
                      <span>· {timeAgo(row.createdAt)}</span>
                      {row.source === 'app' ? <span className="tm-tagsm">app</span> : null}
                    </div>
                    {row.verifiedAt ? (
                      <div className="tm-item-meta">
                        <span>
                          <i className="fa fa-check" aria-hidden="true"></i> Verified by Accounts
                          {row.verifiedByName ? ` (${row.verifiedByName})` : ''} ·{' '}
                          {new Date(row.verifiedAt).toLocaleString()}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* The whole point for an agent: act without opening anything. */}
                  <div className="tm-item-do" onClick={(e) => e.stopPropagation()}>
                    {!isAdmin && row.status === 'pending' ? (
                      <button
                        className="tm-btn tm-btn-blue tm-btn-sm"
                        disabled={busy}
                        onClick={() => ask('verify', row)}
                      >
                        <i className="fa fa-check" aria-hidden="true"></i> Verify
                      </button>
                    ) : null}
                    {isAdmin && row.status === 'verified' ? (
                      <button
                        className="tm-btn tm-btn-green tm-btn-sm"
                        disabled={busy}
                        onClick={() => ask('approve', row)}
                      >
                        <i className="fa fa-check-circle" aria-hidden="true"></i> Approve
                      </button>
                    ) : null}
                    {isAdmin && undoEnabled && row.status === 'approved' ? (
                      <button
                        className="tm-btn tm-btn-light tm-btn-sm"
                        style={{ color: '#b45309', borderColor: '#f59e0b' }}
                        disabled={busy}
                        onClick={() => undoTopup(row)}
                        title="Take this back: Shopping Balance → Available Balance"
                      >
                        <i className="fa fa-undo" aria-hidden="true"></i> Undo
                      </button>
                    ) : null}
                    <button className="tm-btn tm-btn-light tm-btn-sm" onClick={() => openDetail(row)}>
                      Open
                    </button>
                  </div>
                </article>
              );
            })
          )}

          {meta.pages > 1 ? (
            <div className="tm-pager">
              <button className="tm-btn tm-btn-light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹ Prev
              </button>
              <span className="tm-muted">
                Page {meta.page} of {meta.pages} · {fmt(meta.count)} request(s)
              </span>
              <button
                className="tm-btn tm-btn-light"
                disabled={page >= meta.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next ›
              </button>
            </div>
          ) : (
            <div className="tm-pager tm-muted">{fmt(meta.count)} request(s) in this view</div>
          )}
        </div>

        {/* ------------------------------------------------------------ detail */}
        {selected ? (
          <>
            <div className="tm-scrim" onClick={() => setSelected(null)} />
            <aside className="tm-panel">
              <div className="tm-panel-head">
                <span>Request detail</span>
                <button className="tm-x" onClick={() => setSelected(null)} title="Close">×</button>
              </div>

              {!request ? (
                <div className="tm-body tm-muted">Loading…</div>
              ) : (
                <div className="tm-body">
                  <div className="tm-amount">
                    BDT {fmt(request.amountBdt)}
                  </div>

                  {/* Where this request has got to, at a glance. */}
                  <div className="tm-steps">
                    {[
                      { key: 'requested', label: 'Requested', done: true },
                      {
                        key: 'verified',
                        label: request.verifiedAt
                          ? `Verified by Accounts${request.verifiedByName ? ` (${request.verifiedByName})` : ''} · ${new Date(request.verifiedAt).toLocaleString()}`
                          : 'Verified by Accounts',
                        done: Boolean(request.verifiedAt) || ['verified', 'approved'].includes(request.status),
                      },
                      { key: 'approved', label: 'BDT approved', done: request.status === 'approved' },
                    ].map((step) => (
                      <div
                        key={step.key}
                        className={step.done ? 'tm-step-dot tm-step-done' : 'tm-step-dot'}
                      >
                        <i className={`fa ${step.done ? 'fa-check-circle' : 'fa-circle-o'}`} aria-hidden="true"></i>
                        <span>{step.label}</span>
                      </div>
                    ))}
                  </div>
                  {['rejected', 'cancelled'].includes(request.status) ? (
                    <div className="tm-bad">
                      This request was {request.status}
                      {request.rejectReason ? ` — ${request.rejectReason}` : ''}.
                    </div>
                  ) : null}

                  <div className="tm-sec">Member</div>
                  <div className="tm-kv"><span>Name</span><b>{request.name} (@{request.username})</b></div>
                  <div className="tm-kv">
                    <span>Phone</span>
                    <b>
                      {request.phone ? (
                        <>
                          <a href={`tel:${request.phone}`}>{request.phone}</a>{' '}
                          <button className="tm-copy" onClick={() => copy(request.phone, 'Phone')}>
                            <i className="fa fa-clone" aria-hidden="true"></i>
                          </button>
                        </>
                      ) : (
                        '—'
                      )}
                    </b>
                  </div>
                  {member ? (
                    <>
                      <div className="tm-kv">
                        <span>Shopping balance now</span>
                        <b>{fmt(member.shoppingbalance)}</b>
                      </div>
                      <div className="tm-kv">
                        <span>Approved topups before</span>
                        <b>{detail.approvedBefore}</b>
                      </div>
                      <div className="tm-kv">
                        <span>Supervisor</span>
                        <b>
                          {member.isSuperVisor ? 'Yes' : 'No'}
                          {isAdmin ? (
                            <button className="tm-copy tm-linky" disabled={busy} onClick={toggleSupervisor}>
                              {member.isSuperVisor ? 'take away' : 'make one'}
                            </button>
                          ) : null}
                        </b>
                      </div>
                      {member.isBlocked ? (
                        <div className="tm-warn">
                          <i className="fa fa-exclamation-triangle" aria-hidden="true"></i> This account
                          is blocked. Approving would credit a blocked member.
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  <div className="tm-sec">Payment</div>
                  <div className="tm-kv"><span>Method</span><b className="tm-cap">{request.method.replace('_', ' ')}</b></div>
                  {request.methodDetails?.provider ? (
                    <div className="tm-kv"><span>Service / bank</span><b>{request.methodDetails.provider}</b></div>
                  ) : null}
                  {request.methodDetails?.accountNumber ? (
                    <div className="tm-kv">
                      <span>Paid from</span>
                      <b>
                        {request.methodDetails.accountNumber}{' '}
                        <button
                          className="tm-copy"
                          onClick={() => copy(request.methodDetails.accountNumber, 'Account')}
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </b>
                    </div>
                  ) : null}
                  {request.methodDetails?.transactionId ? (
                    <div className="tm-kv">
                      <span>Transaction id</span>
                      <b>
                        {request.methodDetails.transactionId}{' '}
                        <button
                          className="tm-copy"
                          onClick={() => copy(request.methodDetails.transactionId, 'Transaction id')}
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </b>
                    </div>
                  ) : null}
                  {request.methodDetails?.branch ? (
                    <div className="tm-kv"><span>Branch</span><b>{request.methodDetails.branch}</b></div>
                  ) : null}
                  {request.note ? (
                    <div className="tm-kv"><span>Member note</span><b>{request.note}</b></div>
                  ) : null}

                  <div className="tm-sec">Where it came from</div>
                  <div className="tm-kv"><span>Sent</span><b>{new Date(request.createdAt).toLocaleString()}</b></div>
                  <div className="tm-kv"><span>IP</span><b>{request.ip || '—'}</b></div>
                  <div className="tm-kv"><span>From</span><b>{request.source === 'app' ? 'Mobile app' : 'Website'}</b></div>
                  {request.geo?.lat ? (
                    <div className="tm-kv">
                      <span>Location</span>
                      <b>
                        <a
                          href={`https://www.google.com/maps?q=${request.geo.lat},${request.geo.lng}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {request.geo.label || `${request.geo.lat.toFixed(4)}, ${request.geo.lng.toFixed(4)}`}
                        </a>
                        {request.geo.source === 'ip' ? (
                          <span className="tm-muted"> · from the IP</span>
                        ) : null}
                      </b>
                    </div>
                  ) : null}
                  {request.userAgent ? (
                    <div className="tm-kv"><span>Device</span><b className="tm-ua">{request.userAgent}</b></div>
                  ) : null}

                  {request.history?.length ? (
                    <>
                      <div className="tm-sec">What happened</div>
                      {request.history.map((h, i) => (
                        <div className="tm-trail" key={i}>
                          <b className="tm-cap">{h.action}</b>
                          <span className="tm-muted">
                            {new Date(h.at).toLocaleString()}
                            {h.byName ? ` · ${h.byName}` : ''}
                          </span>
                          {h.note ? <div className="tm-muted">{h.note}</div> : null}
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              )}

              {/* The decision sticks to the bottom, so it is reachable without
                  scrolling back through the audit trail. */}
              {/* Each side acts only on its own stage: Accounts on pending, the
                  admin on verified. The server enforces the same split. */}
              {request && (isAdmin ? request.status === 'verified' : request.status === 'pending') ? (
                <div className="tm-decide">
                  <textarea
                    className="tm-input"
                    rows={2}
                    placeholder="Note (required when rejecting)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div className="tm-reasons">
                    {REJECT_REASONS.map((reason) => (
                      <button key={reason} className="tm-reason" onClick={() => setNote(reason)}>
                        {reason}
                      </button>
                    ))}
                  </div>
                  <div className="tm-actions">
                    {!isAdmin ? (
                      <button className="tm-btn tm-btn-blue" disabled={busy} onClick={() => ask('verify', request)}>
                        <i className="fa fa-check" aria-hidden="true"></i> Payment received
                      </button>
                    ) : null}
                    {isAdmin ? (
                      <button className="tm-btn tm-btn-green" disabled={busy} onClick={() => ask('approve', request)}>
                        <i className="fa fa-check-circle" aria-hidden="true"></i> Approve &amp; release
                      </button>
                    ) : null}
                    <button className="tm-btn tm-btn-red" disabled={busy} onClick={() => ask('reject', request)}>
                      <i className="fa fa-times" aria-hidden="true"></i> Reject
                    </button>
                  </div>
                  {!isAdmin ? (
                    <div className="tm-muted">
                      An admin approves the BDT after you confirm the payment.
                    </div>
                  ) : null}
                </div>
              ) : request ? (
                <div className="tm-decide tm-done">
                  {request.status === 'verified'
                    ? 'Verified by Accounts — waiting for an admin to approve or reject it.'
                    : request.status === 'pending'
                    ? 'Still waiting for Accounts to verify the payment.'
                    : 'This request is finished. Nothing further to do.'}
                </div>
              ) : null}
            </aside>
          </>
        ) : null}
      </div>

      </>
      )}

      {/* -------------------------------------------------------- confirm box */}
      {confirm ? (
        <div className="tm-modal-wrap" onClick={() => (busy ? null : setConfirm(null))}>
          <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
            <h5>
              {confirm.kind === 'bulk'
                ? `Approve ${picked.length} topup request(s)?`
                : confirm.kind === 'verify'
                ? 'Confirm the money arrived?'
                : confirm.kind === 'approve'
                ? 'Approve this topup?'
                : 'Reject this request?'}
            </h5>
            <div className="tm-modal-amount">
              BDT {fmt(confirm.kind === 'bulk' ? pickedAmount : confirm.row?.amountBdt)}
            </div>
            {confirm.kind === 'bulk' ? (
              <>
                <div className="tm-kv"><span>Requests</span><b>{picked.length}</b></div>
                <div className="tm-kv"><span>Adds in total</span><b>BDT {fmt(pickedAmount)}</b></div>
                <div className="tm-modal-list">
                  {pickedRows.map((r) => (
                    <div key={r._id}>
                      @{r.username} · BDT {fmt(r.amountBdt)}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="tm-kv"><span>Member</span><b>{confirm.row?.name} (@{confirm.row?.username})</b></div>
                <div className="tm-kv"><span>Method</span><b className="tm-cap">{confirm.row?.method?.replace('_', ' ')}</b></div>
              </>
            )}
            {confirm.kind === 'approve' ? (
              <div className="tm-kv">
                <span>Adds to shopping balance</span>
                <b>BDT {fmt(confirm.row?.amountBdt || 0)}</b>
              </div>
            ) : null}
            {confirm.kind === 'reject' ? (
              <div className="tm-kv"><span>The member is told</span><b>{note}</b></div>
            ) : null}
            <div className={['approve', 'bulk'].includes(confirm.kind) ? 'tm-warn' : 'tm-hint'}>
              {confirm.kind === 'bulk'
                ? 'Every one of these is credited immediately, one after another. None of it can be undone.'
                : confirm.kind === 'approve'
                ? 'The BDT is added to the Shopping Balance immediately and this cannot be undone.'
                : confirm.kind === 'verify'
                ? 'Only confirm this if you can see the money. It then goes to an admin for approval.'
                : 'The member is told the reason and the request is closed.'}
            </div>
            <div className="tm-actions">
              <button className="tm-btn tm-btn-light" disabled={busy} onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                className={
                  ['approve', 'bulk'].includes(confirm.kind)
                    ? 'tm-btn tm-btn-green'
                    : confirm.kind === 'verify'
                    ? 'tm-btn tm-btn-blue'
                    : 'tm-btn tm-btn-red'
                }
                disabled={busy}
                onClick={() =>
                  confirm.kind === 'bulk' ? approveMany() : act(confirm.kind, confirm.row?._id)
                }
              >
                {busy
                  ? bulkAt
                    ? `Approving ${bulkAt.done + 1} of ${bulkAt.total}…`
                    : 'Working…'
                  : 'Yes, go ahead'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const CSS = `
.tm-page { width: 87%; float: right; padding: 16px 20px 60px; box-sizing: border-box; }
@media (max-width: 900px) { .tm-page { width: 100%; float: none; padding: 12px; } }
.tm-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.tm-head h3 { margin: 0 0 2px; }
.tm-head-tools { display: flex; gap: 8px; flex-wrap: wrap; }
.tm-dot { width: 8px; height: 8px; border-radius: 50%; background: #9ca3af; display: inline-block; margin-right: 6px; }
.tm-live-on .tm-dot { background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,.18); }

.tm-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px; margin: 14px 0; }
.tm-stat { text-align: left; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px;
  display: flex; flex-direction: column; gap: 2px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.tm-stat:hover { border-color: #42b4e7; }
.tm-stat-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.tm-stat-plain { cursor: default; }
.tm-stat-plain:hover { border-color: #e5e7eb; }
.tm-stat-label { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; font-weight: 700; }
.tm-stat b { font-size: 22px; }
.tm-stat small { color: #6b7280; font-size: 11.5px; }
.tm-stat-amber { color: #b45309; }
.tm-stat-blue { color: #1d4ed8; }
.tm-stat-green { color: #15803d; }

.tm-views { display: inline-flex; background: #eef2f7; border-radius: 999px; padding: 4px; gap: 4px;
  margin-bottom: 12px; }
.tm-view { border: 0; background: none; border-radius: 999px; padding: 7px 16px; font-size: 13px;
  font-weight: 700; color: #475569; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.tm-view-on { background: #fff; color: #14506e; box-shadow: 0 1px 3px rgba(0,0,0,.12); }

.tm-bulk { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; background: #fff;
  border: 1px dashed #cfd8e3; border-radius: 12px; padding: 9px 13px; margin-bottom: 10px; }
.tm-bulk-on { border-style: solid; border-color: #15803d; background: #f0fdf4; }
.tm-check { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600;
  color: #374151; margin: 0; cursor: pointer; }
.tm-item-check { width: 17px; height: 17px; flex: none; cursor: pointer; }
.tm-linky { font-size: 11.5px; font-weight: 700; text-decoration: underline; margin-left: 6px; }
.tm-modal-list { max-height: 140px; overflow-y: auto; font-size: 12.5px; color: #374151;
  background: #f8fafc; border-radius: 9px; padding: 8px 10px; margin-top: 6px; }

.tm-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.tm-tab { display: inline-flex; align-items: center; gap: 7px; border: 1px solid #e5e7eb; background: #fff;
  border-radius: 999px; padding: 8px 14px; font-size: 13px; font-weight: 700; color: #374151; cursor: pointer; }
.tm-tab i { color: #9ca3af; }
.tm-tab:hover { border-color: #42b4e7; }
.tm-tab-on { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.tm-tab-on i { color: #fff; }
.tm-tab-count { background: rgba(0,0,0,.08); border-radius: 999px; padding: 1px 8px; font-size: 11.5px; }
.tm-tab-on .tm-tab-count { background: rgba(255,255,255,.25); }
.tm-tab-more { margin-left: auto; }
.tm-tab-count-live { background: #dcfce7; color: #166534; }

.tm-filters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; background: #fff;
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px; }
.tm-input { border: 1px solid #cfd8e3; border-radius: 9px; padding: 9px 11px; font-size: 14px;
  box-sizing: border-box; max-width: 100%; }
.tm-input:focus { outline: 3px solid #cdeafa; border-color: #42b4e7; }
.tm-grow { flex: 1 1 240px; }

.tm-btn { border-radius: 9px; padding: 9px 14px; font-weight: 700; cursor: pointer; font-size: 14px;
  border: 1px solid #cfd8e3; background: #fff; text-decoration: none; color: #111827; display: inline-flex;
  align-items: center; gap: 6px; white-space: nowrap; }
.tm-btn:disabled { opacity: .55; cursor: not-allowed; }
.tm-btn-sm { padding: 6px 11px; font-size: 12.5px; }
.tm-btn-primary { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.tm-btn-light { background: #f3f4f6; }
.tm-btn-blue { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.tm-btn-green { background: #15803d; border-color: #15803d; color: #fff; }
.tm-btn-red { background: #b91c1c; border-color: #b91c1c; color: #fff; }

.tm-split { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; align-items: start; }
.tm-split-open { grid-template-columns: minmax(0, 1.35fr) minmax(320px, 1fr); }
@media (max-width: 1100px) { .tm-split-open { grid-template-columns: minmax(0, 1fr); } }

.tm-list { display: flex; flex-direction: column; gap: 10px; }
.tm-item { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e5e7eb;
  border-radius: 12px; padding: 11px 13px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.tm-item:hover { border-color: #42b4e7; }
.tm-item-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.tm-item-icon { flex: none; width: 40px; height: 40px; border-radius: 12px; display: flex;
  align-items: center; justify-content: center; font-size: 16px; background: #f3f4f6; color: #6b7280; }
.tm-icon-pending { background: #fef3c7; color: #b45309; }
.tm-icon-verified { background: #dbeafe; color: #1d4ed8; }
.tm-icon-approved { background: #dcfce7; color: #166534; }
.tm-icon-rejected { background: #fee2e2; color: #991b1b; }
.tm-item-main { flex: 1 1 auto; min-width: 0; }
.tm-item-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.tm-item-amount { font-size: 16px; }
.tm-item-who { font-size: 13.5px; margin-top: 1px; overflow-wrap: anywhere; }
.tm-item-meta { font-size: 11.5px; color: #6b7280; display: flex; gap: 5px; flex-wrap: wrap; margin-top: 2px; }
.tm-tagsm { background: #eef2ff; color: #3730a3; border-radius: 999px; padding: 0 7px; font-weight: 700; }
.tm-item-do { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; flex: none; }
@media (max-width: 700px) {
  .tm-item { flex-wrap: wrap; }
  .tm-item-do { width: 100%; justify-content: stretch; }
  .tm-item-do .tm-btn { flex: 1; justify-content: center; }
}

.tm-chip { border-radius: 999px; padding: 2px 9px; font-size: 11px; font-weight: 700; }
.tm-cap { text-transform: capitalize; }
.tm-muted { color: #6b7280; font-size: 12.5px; }
.tm-bad { color: #991b1b; font-size: 12.5px; font-weight: 600; }
.tm-empty { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px 16px; text-align: center; }
.tm-empty-icon { font-size: 30px; color: #b6d7e8; margin-bottom: 6px; }
.tm-pager { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 10px; }

.tm-scrim { display: none; }
.tm-panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;
  position: sticky; top: 12px; max-height: calc(100vh - 24px); display: flex; flex-direction: column; }
.tm-panel-head { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px;
  background: #f9fafb; border-bottom: 1px solid #e5e7eb; font-weight: 700; font-size: 12px;
  text-transform: uppercase; color: #374151; }
.tm-x { border: 0; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #6b7280; }
.tm-body { padding: 14px; display: flex; flex-direction: column; gap: 4px; overflow-y: auto; }
.tm-amount { font-size: 24px; font-weight: 800; }
.tm-steps { display: flex; flex-direction: column; gap: 4px; margin: 8px 0 4px; }
.tm-step-dot { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #9ca3af; }
.tm-step-done { color: #166534; font-weight: 700; }
.tm-sec { margin-top: 12px; font-size: 11px; text-transform: uppercase; color: #6b7280; font-weight: 800;
  letter-spacing: .04em; }
.tm-kv { display: flex; justify-content: space-between; gap: 12px; font-size: 13px;
  border-bottom: 1px solid #f3f4f6; padding: 5px 0; }
.tm-kv span { color: #6b7280; flex: none; }
.tm-kv b { text-align: right; overflow-wrap: anywhere; }
.tm-copy { border: 0; background: none; color: #42b4e7; cursor: pointer; padding: 0 2px; }
.tm-ua { font-size: 11px; font-weight: 400; }
.tm-trail { font-size: 12.5px; border-left: 2px solid #e5e7eb; padding: 3px 0 7px 9px; display: flex;
  flex-direction: column; }
.tm-warn { background: #fef3c7; border: 1px solid #fcd34d; color: #92400e; border-radius: 9px;
  padding: 9px 11px; font-size: 12.5px; margin-top: 6px; }
.tm-hint { background: #e6f5fd; border: 1px solid #bfe4f8; color: #14506e; border-radius: 9px;
  padding: 9px 11px; font-size: 12.5px; margin-top: 6px; }

.tm-decide { border-top: 1px solid #e5e7eb; background: #f9fafb; padding: 12px 14px; display: flex;
  flex-direction: column; gap: 8px; }
.tm-decide .tm-input { width: 100%; }
.tm-reasons { display: flex; gap: 6px; flex-wrap: wrap; }
.tm-reason { border: 1px dashed #cfd8e3; background: #fff; border-radius: 999px; padding: 3px 10px;
  font-size: 11.5px; color: #374151; cursor: pointer; }
.tm-reason:hover { border-color: #42b4e7; color: #14506e; }
.tm-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.tm-actions .tm-btn { flex: 1 1 auto; justify-content: center; }
.tm-done { color: #374151; font-size: 12.5px; }

/* On a narrow screen the panel is a sheet over the queue, not a squeezed column. */
@media (max-width: 1100px) {
  .tm-scrim { display: block; position: fixed; inset: 0; background: rgba(15,23,42,.45); z-index: 40; }
  .tm-panel { position: fixed; z-index: 41; top: auto; bottom: 0; left: 0; right: 0; border-radius: 16px 16px 0 0;
    max-height: 88vh; }
}

.tm-modal-wrap { position: fixed; inset: 0; background: rgba(15,23,42,.5); display: flex; align-items: center;
  justify-content: center; padding: 16px; z-index: 60; }
.tm-modal { background: #fff; border-radius: 16px; padding: 18px; width: 100%; max-width: 420px;
  display: flex; flex-direction: column; gap: 4px; box-shadow: 0 10px 40px rgba(0,0,0,.25); }
.tm-modal h5 { margin: 0 0 4px; }
.tm-modal-amount { font-size: 26px; font-weight: 800; margin-bottom: 6px; }
.tm-modal .tm-actions { margin-top: 12px; }
`;
